// CHANGE v2 §23: queries simplificadas — produtos/estratégias removidos;
// substituídos por allocations + health + ai-plan.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { invalidateGuidedFlow } from '../guidedFlow/queries';
import { unwrap } from '../../data/api';

import {
  allocationsAPI,
  buildGoalAPI,
  configAPI,
  efAlertsAPI,
  healthAPI,
  planAPI,
  targetEstimateAPI,
  wizardAPI,
} from '../../data/emergencyFund';

export const emergencyFundKeys = {
  all: ['emergencyFund'],
  config: () => [...emergencyFundKeys.all, 'config'],
  health: () => [...emergencyFundKeys.all, 'health'],
  allocationSetup: () => [...emergencyFundKeys.all, 'allocationSetup'],
  buildGoalSuggestion: () => [...emergencyFundKeys.all, 'buildGoalSuggestion'],
  alerts: () => [...emergencyFundKeys.all, 'alerts'],
};


export function useEmergencyFundTargetEstimateQuery(queryOptions = {}) {
  return useQuery({
    queryKey: [...emergencyFundKeys.all, 'targetEstimate'],
    queryFn: () => targetEstimateAPI.get().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useEmergencyFundConfigQuery(queryOptions = {}) {
  return useQuery({
    queryKey: emergencyFundKeys.config(),
    queryFn: () => configAPI.get().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useFundHealthQuery(queryOptions = {}) {
  return useQuery({
    queryKey: emergencyFundKeys.health(),
    queryFn: () => healthAPI.get().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useAllocationSetupQuery(queryOptions = {}) {
  return useQuery({
    queryKey: emergencyFundKeys.allocationSetup(),
    queryFn: () => allocationsAPI.getSetup().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useBuildGoalSuggestionQuery(queryOptions = {}) {
  return useQuery({
    queryKey: emergencyFundKeys.buildGoalSuggestion(),
    queryFn: () => buildGoalAPI.getSuggestion().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useEmergencyFundAlertsQuery(queryOptions = {}) {
  return useQuery({
    queryKey: emergencyFundKeys.alerts(),
    queryFn: () => efAlertsAPI.list().then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

function invalidateAll(qc) {
  qc.invalidateQueries({ queryKey: emergencyFundKeys.all, exact: false });
  invalidateGuidedFlow(qc);
}

export function useUpdateConfigMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => configAPI.update(payload).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useBulkAllocationsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items) =>
      allocationsAPI.bulkSave(items).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteAllocationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => allocationsAPI.remove(id).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAdvanceWizardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (step) => wizardAPI.advance(step).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSimulatePlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (riskProfile) =>
      planAPI.simulate(riskProfile).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useToggleSimulationFavoriteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => planAPI.toggleFavorite(id).then((r) => unwrap(r)),
    onSuccess: () => invalidateAll(qc),
  });
}
